import { Fragment, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Search } from "lucide-react";
import { auditApi, auditQueryKeys } from "../../shared/api/audit";
import { Link } from "react-router-dom";
import { auditFieldRows, auditValueHref, shortAuditValue } from "./auditChanges";
import type { AuditListFilter } from "../../shared/types/audit";
import { formatDateTime } from "../../shared/format";
import { PageHeader } from "../../shared/ui/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const PAGE_SIZE = 25;

export function AuditLogPage() {
  const [q, setQ] = useState("");
  /*
   * Dòng đang mở để xem trước/sau (#19111). Một dòng tại một thời điểm: đây là màn hình để ĐỐI
   * CHIẾU một sự kiện, và mở sẵn nhiều dòng chỉ làm bảng dài ra.
   */
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQ(q.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const filter: AuditListFilter = useMemo(
    () => ({ q: debouncedQ || null }),
    [debouncedQ]
  );

  const query = useQuery({
    queryKey: auditQueryKeys.list(filter, page),
    queryFn: () => auditApi.list(filter, { page, size: PAGE_SIZE }),
    placeholderData: (prev) => prev,
  });

  const rows = query.data?.content ?? [];
  const totalPages = query.data?.totalPages ?? 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Audit log"
        description="Lịch sử hành động — Thông tư 46 + Nghị định 13 PDPD §10."
      />

      <div className="relative max-w-md">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          type="search"
          className="pl-9"
          placeholder="Tìm theo action / entity id…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {query.isError && (
        <p className="text-sm text-destructive">
          Không tải được nhật ký kiểm toán.
        </p>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Thời gian</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Hành động</TableHead>
                <TableHead>Đối tượng</TableHead>
                <TableHead>Kết quả</TableHead>
                <TableHead>IP</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.isLoading && (
                <TableRow>
                  <TableCell colSpan={6} className="text-muted-foreground">
                    Đang tải…
                  </TableCell>
                </TableRow>
              )}
              {!query.isLoading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-muted-foreground">
                    Chưa có bản ghi.
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r) => {
                const changes = auditFieldRows(r.before, r.after);
                const expandable = changes.length > 0 || !!r.before || !!r.after;
                const open = openRow === r.id;
                return (
                <Fragment key={r.id}>
                <TableRow
                  data-testid="audit-row"
                  className={expandable ? "cursor-pointer" : undefined}
                  onClick={
                    expandable
                      ? () => setOpenRow((cur) => (cur === r.id ? null : r.id))
                      : undefined
                  }
                >
                  <TableCell className="whitespace-nowrap">
                    {expandable && (
                      <span
                        aria-hidden="true"
                        className="mr-1 inline-block align-middle text-muted-foreground"
                      >
                        {open ? (
                          <ChevronDown className="size-4" />
                        ) : (
                          <ChevronRight className="size-4" />
                        )}
                      </span>
                    )}
                    {formatDateTime(r.occurredAt)}
                  </TableCell>
                  <TableCell>
                    {/*
                      Tên + vai trò chụp lúc ghi (#19111). Dòng cũ không có — hiện lại đúng dạng
                      trước đây (loại actor + 8 ký tự đầu của UUID) chứ không đi tra bù: tra lúc đọc
                      là cách anh Minh đã bác, vì nó trả lời "bây giờ họ là ai", không phải "lúc ấy".
                    */}
                    {r.actorName ? (
                      <>
                        <span>{r.actorName}</span>
                        {r.actorRole && (
                          <span className="text-muted-foreground"> · {r.actorRole}</span>
                        )}
                      </>
                    ) : (
                      <>
                        {r.actorType}
                        {r.actorId && (
                          <span className="font-mono text-muted-foreground">
                            {" "}
                            · {r.actorId.slice(0, 8)}
                          </span>
                        )}
                      </>
                    )}
                  </TableCell>
                  <TableCell className="font-mono">{r.action}</TableCell>
                  <TableCell>
                    {r.entityType}
                    {r.entityId && (
                      <span className="text-muted-foreground"> · {r.entityId}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={r.result === "SUCCESS" ? "success" : "destructive"}
                    >
                      {r.result}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono">{r.ip ?? "—"}</TableCell>
                </TableRow>
                {open && (
                  <TableRow className="bg-muted/40 hover:bg-muted/40">
                    <TableCell colSpan={6}>
                      {/*
                        Bảng "trước → sau" (#19111). Chỉ ID, không tên: ticket đã chốt không chép
                        tên bác sĩ / bệnh nhân vào nhật ký, nên màn hình cũng không dựng lại chúng.
                        ID bấm được để mở hồ sơ hiện tại (#19683).
                      */}
                      {changes.length > 0 ? (
                        <div className="space-y-1" data-testid="audit-changes">
                          {changes.map((c) => (
                            <div
                              key={c.key}
                              className="grid grid-cols-[12rem_1fr_1fr] items-baseline gap-2 text-sm"
                            >
                              <span className="text-muted-foreground">{c.label}</span>
                              <span
                                className={
                                  c.changed
                                    ? "font-mono line-through decoration-muted-foreground"
                                    : "font-mono text-muted-foreground"
                                }
                                title={c.from ?? undefined}
                              >
                                <AuditValue field={c.key} value={c.from} />
                              </span>
                              <span
                                className={c.changed ? "font-mono font-semibold" : "font-mono text-muted-foreground"}
                                title={c.to ?? undefined}
                              >
                                <AuditValue field={c.key} value={c.to} />
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        // Dữ liệu có nhưng không phải object — in nguyên văn còn hơn giấu đi.
                        <pre className="overflow-x-auto text-xs">
                          {r.before ?? "—"} → {r.after ?? "—"}
                        </pre>
                      )}
                    </TableCell>
                  </TableRow>
                )}
                </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            ← Trước
          </Button>
          <span className="text-sm text-muted-foreground">
            Trang {page + 1} / {totalPages}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={page + 1 >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Sau →
          </Button>
        </div>
      )}
    </div>
  );
}

/** Một giá trị trong bảng trước/sau: ID rút gọn, thành link khi biết trỏ đi đâu (#19683). */
function AuditValue({ field, value }: { field: string; value: string | null }) {
  const href = auditValueHref(field, value);
  if (!href) return <>{shortAuditValue(value)}</>;
  return (
    <Link
      to={href}
      className="underline decoration-dotted underline-offset-2 hover:text-primary"
      onClick={(e) => e.stopPropagation()}
    >
      {shortAuditValue(value)}
    </Link>
  );
}
